import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Loader2, X, Phone, Globe, Settings, MessageCircle, RefreshCw, Eye } from "lucide-react";
import { toast } from "react-toastify";
import { checkSMSMessages, clearSMSMessages } from "../../../RTK/orders/GetAllOrders";
import SMSMessagesPopup from "./SMSMessagesPopup";

const SMMOrderDetailsPopup = ({ order, onClose, StatusBadge, onUpdateStatus }) => {
  const dispatch = useDispatch();
  const { smsMessages } = useSelector((state) => state.orders);
  
  const orderDetails = order?.[0]?.order_data || {};
  const userData = order?.[0]?.user_data || {};
  const order_id = order?.[0]?.order_id;
  const [selectedStatus, setSelectedStatus] = useState(orderDetails?.status);
  const [isLoading, setIsLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isCheckingSMS, setIsCheckingSMS] = useState(false);
  const [showSMSPopup, setShowSMSPopup] = useState(false);

  useEffect(() => {
    setSelectedStatus(orderDetails?.status);
    // Clear SMS messages when component mounts
    dispatch(clearSMSMessages());
  }, [orderDetails, dispatch]);

  const statusOptions = ["pending", "completed", "cancelled"];

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const handleStatusUpdate = async () => {
    if (selectedStatus !== orderDetails?.status) {
      setIsLoading(true);
      try {
        await onUpdateStatus(order_id, selectedStatus);
        toast.success(`تم تحديث الحالة إلى: ${selectedStatus}`);
        onClose();
      } catch (error) {
        toast.error("فشل في تحديث الحالة. يرجى المحاولة مرة أخرى.");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCheckSMS = async () => {
    setIsCheckingSMS(true);
    try {
      await dispatch(checkSMSMessages(orderDetails.transaction_id)).unwrap();
      toast.success("تم فحص رسائل SMS بنجاح!");
      // Show SMS popup after successful check
      setShowSMSPopup(true);
    } catch (error) {
      toast.error("فشل في فحص رسائل SMS. يرجى المحاولة مرة أخرى.");
    } finally {
      setIsCheckingSMS(false);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    dispatch(clearSMSMessages());
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 400);
  };

  // Parse product requirements data for SMM orders
  const smmData = orderDetails.product_requirements_data || {};

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[4200] p-4">
        <div
          className={`${
            isClosing ? "animate-close" : "animate-popup"
          } bg-slate-900 rounded-2xl w-full max-w-lg border border-slate-800 shadow-xl animate-in zoom-in-95 z-[4300]`}
        >
          <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">تفاصيل طلب SMS</h3>
              <span className="text-sm font-serif text-gray-500">
                #{orderDetails.transaction_id}
              </span>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-red-800 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <StatusBadge status={selectedStatus} />
              </div>

              <div className="bg-gray-800 rounded-2xl p-1 text-sm px-2">
                {orderDetails.execution_time}
              </div>

              <div className="relative">
                <select
                  id="status"
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  className="bg-slate-800 text-white py-0 px-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* SMS Service Details */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                {/* SMS Details */}
                <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-3">
                  <div className="text-sm text-slate-400 text-center mb-3">
                    تفاصيل SMS
                  </div>
                  
                  {smmData.phone && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-blue-400" />
                        <span className="text-slate-400">رقم الهاتف:</span>
                      </div>
                      <span className="text-white font-mono">{smmData.phone}</span>
                    </div>
                  )}

                  {smmData.country && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-green-400" />
                        <span className="text-slate-400">الدولة:</span>
                      </div>
                      <span className="text-white capitalize">{smmData.country}</span>
                    </div>
                  )}

                  {smmData.operator && (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Settings className="w-4 h-4 text-yellow-400" />
                        <span className="text-slate-400">المشغل:</span>
                      </div>
                      <span className="text-white">{smmData.operator}</span>
                    </div>
                  )}

                  {smmData.service && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">الخدمة:</span>
                      <span className="text-white">{smmData.service}</span>
                    </div>
                  )}

                  {smmData.original_price_rub && (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400">السعر الأصلي (RUB):</span>
                      <span className="text-white">{smmData.original_price_rub}</span>
                    </div>
                  )}

                  {/* Check SMS Button */}
                  <div className="pt-2">
                    <button
                      onClick={handleCheckSMS}
                      disabled={isCheckingSMS}
                      className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-800 text-white py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {isCheckingSMS ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          جاري التحقق...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          تحقق من الرسائل
                        </>
                      )}
                    </button>
                  </div>
                </div>

                

                {/* Price Details */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-1 flex items-center justify-between">
                    <div className="">
                      <div className="text-sm text-slate-400">السعر</div>
                      <div className="text-emerald-400 font-medium mt-2">
                        ${orderDetails.total_price}
                      </div>
                    </div>

                    <div className="">
                      <div className="text-sm text-slate-400 text-right">
                        الربح
                      </div>
                      <div className="text-purple-400 font-medium mt-2">
                        ${orderDetails.profit_margin}
                      </div>
                    </div>
                  </div>
                </div>

                {/* User and Date */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-1">
                    <div className="text-sm text-slate-400">المستخدم</div>
                    <div className="text-white font-medium">
                      {userData.username}@
                    </div>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-1">
                    <div className="text-sm text-slate-400 text-right">
                      التاريخ
                    </div>
                    <div className="text-slate-300 text-sm text-right">
                      {orderDetails.formatted_order_date}
                    </div>
                  </div>
                </div>

                {/* Agent Details */}
                <div className="bg-slate-800/50 rounded-xl py-2 px-4 space-y-1 flex items-center justify-between">
                  <div className="">
                    <div className="text-sm text-slate-400">الوكيل</div>
                    <div className="text-white font-medium mt-2">
                      {orderDetails.cheapest_agent_name}
                    </div>
                  </div>

                  <div className="">
                    <div className="text-sm text-slate-400 text-right">
                      معرف المعاملة للوكيل
                    </div>
                    <div className="text-white font-medium mt-2 text-right">
                      {orderDetails.order_agent_id}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Update Status Button */}
            <div className="flex justify-end">
              <button
                onClick={handleStatusUpdate}
                disabled={isLoading || selectedStatus === orderDetails?.status}
                className={`w-full px-4 py-2 rounded-lg transition-colors ${
                  isLoading || selectedStatus === orderDetails?.status
                    ? "bg-slate-600 cursor-not-allowed"
                    : "bg-emerald-700 hover:bg-emerald-500"
                } text-white`}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    جاري التحديث...
                  </div>
                ) : (
                  "تحديث الحالة"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* SMS Messages Popup */}
      {showSMSPopup && (
        <SMSMessagesPopup
          smsMessages={smsMessages}
          onClose={() => setShowSMSPopup(false)}
        />
      )}
    </>
  );
};

export default SMMOrderDetailsPopup;